import React, { useState } from 'react';

import { Box, Tab, Tabs, Typography } from '@mui/material';

import {
  CommonComponentProps,
  ComponentConfig,
  TabConfig,
} from '../pages/types';
import { cleanComponentData } from '../utils/component-utils';
import EditableMarkdownText from './editable-markdown-text';

interface ContainerProps extends CommonComponentProps {
  columns?: number;
  spacing?: number;
  maxWidth?: string;
  minColumns?: number;
  items?: ComponentConfig[];
  useTabs?: boolean;
  tabs?: TabConfig[];
  renderComponent?: (
    component: ComponentConfig,
    index: number,
    context?: any
  ) => React.ReactNode;
}

const Container: React.FC<ContainerProps> = ({
  id,
  columns = 2,
  spacing = 2,
  maxWidth,
  minColumns,
  items = [],
  useTabs = false,
  tabs = [],
  renderComponent,
  sx,
  isEditable,
  onPropsChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const safeItems = items || [];
  const safeTabs = tabs || [];

  // Default render function if none provided
  const defaultRenderComponent = (
    component: ComponentConfig,
    index: number,
    _context?: any
  ): React.ReactNode => {
    return (
      <Box
        key={index}
        sx={{ p: 1, border: '1px dashed #ccc', minHeight: '50px' }}
      >
        <Typography variant="body2">
          {component.type} Component (Placeholder)
        </Typography>
      </Box>
    );
  };

  const actualRenderComponent = renderComponent || defaultRenderComponent;

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Get items to render (either from active tab or regular items)
  const getItemsToRender = () => {
    if (useTabs && safeTabs.length > 0 && safeTabs[activeTab]) {
      return safeTabs[activeTab].items || [];
    }
    return safeItems;
  };
  const renderItems = (itemsToRender: ComponentConfig[], context?: any) => (
    <Box
      sx={{
        display: 'grid',
        // Responsive grid template columns:
        // - xs to sm: minColumns override or single column (vertical layout)
        // - md and up: Multiple columns based on the 'columns' prop
        gridTemplateColumns: {
          xs: minColumns ? `repeat(${minColumns}, 1fr)` : '1fr', // Mobile: use minColumns if set, otherwise single column
          sm: `repeat(${Math.min(columns, 6)}, 1fr)`, // Desktop: horizontal layout
        },
        gap: `${spacing * 8}px`,
        width: '100%',
        justifyItems: 'center', // This centers the items horizontally in their grid cells
        margin: '0 auto', // This helps center the entire grid
      }}
    >
      {itemsToRender.map((item, index) => {
        const cleanedItem = cleanComponentData(item);

        // Add a check for empty props or missing required data
        const isEmpty =
          !cleanedItem.props ||
          (cleanedItem.type === 'TextBox' &&
            (!cleanedItem.props.texts || cleanedItem.props.texts.length === 0));

        return (
          <Box
            key={index}
            sx={{
              boxSizing: 'border-box',
              minHeight: '10px',
              width: '100%',
            }}
          >
            {isEmpty ? (
              <Typography color="error">
                Empty component or missing data
              </Typography>
            ) : (
              actualRenderComponent(cleanedItem, index, context)
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      id={id}
      sx={{
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: maxWidth || 1100,
        ...sx,
      }}
    >
      {/* Render tabs if using tabs */}
      {useTabs && safeTabs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {safeTabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <EditableMarkdownText
                    content={tab.label}
                    onSave={(newLabel) => {
                      if (onPropsChange) {
                        const newTabs = [...safeTabs];
                        newTabs[index] = { ...newTabs[index], label: newLabel };
                        onPropsChange({ tabs: newTabs });
                      }
                    }}
                    isEditable={!!isEditable && activeTab === index}
                    wrapper="span"
                  />
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Render content */}
      {(() => {
        if (useTabs && safeTabs.length > 0 && safeTabs[activeTab]) {
          return renderItems(safeTabs[activeTab].items || [], {
            type: 'tab',
            tabIndex: activeTab,
          });
        }
        return renderItems(safeItems);
      })()}
    </Box>
  );
};

export default Container;
